import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);

export function CartProvider({ children }) {
    const { user } = useAuth();
    const [cart, setCart] = useState({ items: [], subtotal: 0 });
    const [wishlist, setWishlist] = useState({ items: [], products: [] });
    const [isOpen, setIsOpen] = useState(false);

    const refresh = useCallback(async () => {
        if (!user) {
            setCart({ items: [], subtotal: 0 });
            setWishlist({ items: [], products: [] });
            return;
        }
        try {
            const [c, w] = await Promise.all([api.get("/cart"), api.get("/wishlist")]);
            setCart(c.data);
            setWishlist(w.data);
        } catch {}
    }, [user]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const addToCart = async (productId, quantity = 1, size = null, color = null) => {
        if (!user) return { ok: false, error: "Please sign in to shop." };
        const { data } = await api.post("/cart/add", { product_id: productId, quantity, size, color });
        setCart(data);
        setIsOpen(true);
        return { ok: true };
    };

    const updateQty = async (productId, quantity, size = null, color = null) => {
        const { data } = await api.post("/cart/update", { product_id: productId, quantity, size, color });
        setCart(data);
    };

    const clearCart = async () => {
        await api.post("/cart/clear");
        setCart({ items: [], subtotal: 0 });
    };

    const toggleWishlist = async (productId) => {
        if (!user) return { ok: false, error: "Sign in to save favourites." };
        const { data } = await api.post("/wishlist/toggle", { product_id: productId });
        setWishlist((w) => ({ ...w, items: data.items }));
        // refresh products list
        const w = await api.get("/wishlist");
        setWishlist(w.data);
        return { ok: true };
    };

    const inWishlist = (productId) => wishlist.items.includes(productId);

    const count = cart.items.reduce((acc, it) => acc + (it.quantity || 0), 0);

    return (
        <CartContext.Provider
            value={{
                cart,
                wishlist,
                count,
                isOpen,
                setIsOpen,
                addToCart,
                updateQty,
                clearCart,
                toggleWishlist,
                inWishlist,
                refresh,
            }}
        >
            {children}
        </CartContext.Provider>
    );
}

export const useCart = () => useContext(CartContext);
